import React, {ReactNode, useCallback} from "react";

import {
    BaseNode,
    BaseNodeContent,
    BaseNodeHeader,
    BaseNodeHeaderTitle,
} from "@/components/base-node";
import {BaseHandle} from "@/components/base-handle";
import {cn} from "@/lib/utils";
import {type NodeProps, useNodeId, useReactFlow} from "@xyflow/react";
import {Button} from "@/components/ui/button";
import {Pencil, Repeat, Trash} from "lucide-react";
import {useSetRecoilState} from "recoil";
import editingOperatorIdState from "@/view/workflow/editingOperatorContext";
import OperatorIcon from "@/view/workflow/components/OperatorIcon";
import type {OperatorNode} from "@/view/workflow/components/workflowGraph/types";

type BaseOperatorNodeProps = NodeProps<OperatorNode> & {
    children?: ReactNode;
}

const OperatorNodeBase = (props: BaseOperatorNodeProps)=> {
    const {data, sourcePosition, targetPosition, isConnectable, children} = props;
    const executionOrders = data.executionOrders ?? []

    const id = useNodeId();
    const {deleteElements} = useReactFlow();
    const setEditingOperatorId = useSetRecoilState(editingOperatorIdState);

    const onClickUpdate = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        if (!id)
            return;
        setEditingOperatorId(id);
    }, [id, setEditingOperatorId]);

    const onClickDelete = useCallback(
        async (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            if (!id) {
                return;
            }

            setEditingOperatorId((prev) => (prev === id ? undefined : prev));
            await deleteElements({nodes: [{id}]});
        },
        [id, deleteElements, setEditingOperatorId]
    );

    const showTarget = Boolean(targetPosition) && data.type !== "input";

    return (
        <BaseNode
            className={cn(
                "w-96 px-3 py-2",
                data.borderWidth === 4 ? "border-4" : "border-2",
            )}
            style={
                data.borderColor ? {borderColor: data.borderColor} : undefined
            }
        >
            {showTarget && (
                <BaseHandle
                    key={`target-${targetPosition}`}
                    id="target"
                    type="target"
                    position={targetPosition!}
                    isConnectable={isConnectable}
                    className="!w-3 !h-3"
                />
            )}
            {sourcePosition && (
                <BaseHandle
                    key={`source-${sourcePosition}`}
                    id="source"
                    type="source"
                    position={sourcePosition}
                    isConnectable={isConnectable}
                    className="!w-3 !h-3"
                />
            )}

            <BaseNodeHeader className="border-b">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                    <span className="shrink-0 [&>*]:size-5">
                        <OperatorIcon type={data.type}/>
                    </span>
                    <BaseNodeHeaderTitle className="min-w-0 truncate">
                        {data.name}
                    </BaseNodeHeaderTitle>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                    {data.duration !== undefined && (
                        <span className="text-black text-xs tabular-nums">
                            {data.duration ?? 0}ms
                        </span>
                    )}

                    {data.editable && (
                        <Button
                            variant="ghost"
                            className="nodrag p-1"
                            onClick={onClickUpdate}
                            aria-label="Edit Node"
                            title="Edit Node"
                        >
                            <Pencil className="size-4"/>
                        </Button>
                    )}
                    {data.editable && data.type !== "input" && (
                        <Button
                            variant="ghost"
                            className="text-red-500 nodrag p-1"
                            onClick={onClickDelete}
                            aria-label="Delete Node"
                            title="Delete Node"
                        >
                            <Trash className="size-4"/>
                        </Button>
                    )}
                </div>
            </BaseNodeHeader>

            <BaseNodeContent>
                <div className="flex items-center justify-between gap-2 text-sm">
                    <span>{data.type}</span>
                    {executionOrders.length === 0 && data.recursionLimit != null && (
                        <div className="flex items-center gap-1 rounded border border-orange-200 bg-orange-50 px-1.5 py-0.5 font-medium text-orange-700">
                            <Repeat size={16}/>
                            <span>{data.recursionLimit}</span>
                        </div>
                    )}
                    {executionOrders.length > 0 &&
                        <div className="flex flex-wrap justify-end gap-1">
                            {executionOrders.map((order) => (
                                <span
                                    key={order}
                                    className="rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-xs font-semibold leading-none text-blue-700 shadow-sm"
                                >
                                {order}
                            </span>
                            ))}
                        </div>
                    }
                </div>
                {children}
            </BaseNodeContent>
        </BaseNode>
    );
}

export default OperatorNodeBase;